import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import privateApi from "../../apis/privateApi";
import Spinner from "../../components/Spinner/Spinner";
import Notification from "../../components/Notification/Notification";
import "./LGABudgetEdit.css";

const LGABudgetEdit = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const budgetId = location?.state?.budgetId;

    const [lgaName, setLgaName] = useState("");
    const [formData, setFormData] = useState({
        year: "",
        type: "Original",
        revisionNumber: "",
        remark: ""
    });
    const [hasBudgetLines, setHasBudgetLines] = useState(false);

    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [notification, setNotification] = useState({
        show: false,
        message: "",
        type: "info"
    });
    const [yearOptions, setYearOptions] = useState([]);

    // Populate year options
    useEffect(() => {
        const currentYear = new Date().getFullYear();
        setYearOptions(Array.from({ length: 5 }, (_, i) => currentYear - i));
    }, []);

    // Fetch budget details
    useEffect(() => {
        const fetchBudget = async () => {
            try {
                setLoading(true);

                const { data } = await privateApi.get(`/api/lga/budgets/${budgetId}`);
                const budget = data.data;

                setLgaName(budget.lgaId?.name || "");
                setHasBudgetLines(budget.budgetLinesCount > 0);

                setFormData({
                    year: budget.year ?? "",
                    type: budget.type ?? "Original",
                    revisionNumber: budget.revisionNumber ?? "",
                    remark: budget.remark ?? ""
                });

            } catch (err) {
                setNotification({
                    show: true,
                    message: err.response?.data?.message || "Failed to load LGA budget.",
                    type: "error"
                });
            } finally {
                setLoading(false);
            }
        };

        if (budgetId) fetchBudget();
    }, [budgetId]);

    // Handle form field changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Submit updated budget
    const handleSubmit = async (e) => {
        e.preventDefault();
        setActionLoading(true);

        try {
            const payload = {
                remark: formData.remark
            };

            const { data } = await privateApi.patch(`/api/lga/budgets/${budgetId}`, payload);

            setNotification({
                show: true,
                message: `Budget ${data.data.type} ${data.data.year} rev ${data.data.revisionNumber} updated successfully!`,
                type: "success"
            });

        } catch (err) {
            setNotification({
                show: true,
                message: err.response?.data?.message || "Failed to update LGA budget.",
                type: "error"
            });
        } finally {
            setActionLoading(false);
        }
    };

    const closeNotification = () => {
        setNotification(prev => ({ ...prev, show: false }));
    };

    if (loading) return <Spinner loadingInfo="Loading LGA Budget Details..." />;

    return (
        <div className="lga-budget-edit">

            <Notification
                show={notification.show}
                message={notification.message}
                type={notification.type}
                onClose={closeNotification}
            />

            <h2>Edit LGA Budget</h2>

            {lgaName && (
                <div className="lga-budget-edit-lga">
                    LGA: <strong>{lgaName}</strong>
                </div>
            )}

            <form onSubmit={handleSubmit}>

                <div className="lga-budget-edit-form-group">
                    <label htmlFor="year">Year</label>
                    <select
                        id="year"
                        name="year"
                        value={formData.year}
                        onChange={handleChange}
                        required
                        disabled={hasBudgetLines}
                    >
                        <option value="">Select Year</option>
                        {yearOptions.map(y => (
                            <option key={y} value={y}>{y}</option>
                        ))}
                    </select>
                </div>

                <div className="lga-budget-edit-form-group">
                    <label htmlFor="type">Type</label>
                    <select
                        id="type"
                        name="type"
                        value={formData.type}
                        onChange={handleChange}
                        disabled={hasBudgetLines}
                    >
                        <option value="Original">Original</option>
                        <option value="Supplementary">Supplementary</option>
                    </select>
                </div>

                <div className="lga-budget-edit-form-group">
                    <label htmlFor="revisionNumber">Revision Number</label>
                    <input
                        type="text"
                        id="revisionNumber"
                        name="revisionNumber"
                        value={formData.revisionNumber}
                        readOnly
                    />
                </div>

                <div className="lga-budget-edit-form-group">
                    <label htmlFor="remark">Remark</label>
                    <textarea
                        id="remark"
                        name="remark"
                        rows="3"
                        value={formData.remark}
                        onChange={handleChange}
                        placeholder="Optional remark..."
                    />
                </div>

                <div className="form-actions">

                    <button
                        type="submit"
                        className="btn"
                        disabled={actionLoading}
                    >
                        {actionLoading ? "Saving..." : "Save Changes"}
                    </button>

                    <button
                        type="button"
                        className="btn btn-cancel"
                        onClick={() => navigate(-1)}
                    >
                        Cancel
                    </button>

                </div>

            </form>

        </div>
    );
};

export default LGABudgetEdit;